import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Alert, Box, Button, CircularProgress, IconButton,
  Paper, Stack, Typography,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { useTranslation } from "react-i18next";

import api from "../api/axios";
import { pageWrap } from "../utils/pageStyles";
import WebTelnetConsole from "../components/console/WebTelnetConsole";
import NoVncViewer      from "../components/console/NoVncViewer";

export default function LabConsolePage() {
  const { t } = useTranslation();
  const { reservationId, nodeId } = useParams();
  const navigate = useNavigate();

  const rid = useMemo(() => {
    const n = Number(reservationId);
    return Number.isFinite(n) ? n : null;
  }, [reservationId]);

  const [loading, setLoading] = useState(true);
  const [node,    setNode]    = useState(null);   // entrée du node cible
  const [error,   setError]   = useState("");

  useEffect(() => {
    if (!rid) return;
    // Garde anti-réponse-obsolète : si nodeId change (ou le composant est
    // démonté) avant la résolution de cette requête, son résultat ne doit
    // jamais écraser l'état d'une sélection plus récente (race condition
    // A→B→A / A→B→C sur navigation rapide entre nœuds).
    let ignore = false;
    (async () => {
      setLoading(true);
      setError("");
      try {
        // /nodes retourne déjà console_host, console_port, console_type pour chaque nœud
        const res  = await api.get(`/gns3/reservations/${rid}/nodes`);
        if (ignore) return;
        const list = res?.data?.nodes || [];
        const found = list.find((n) => String(n.node_id) === String(nodeId));
        if (!found) throw new Error(`Node ${nodeId} not found in reservation ${rid}`);
        setNode(found);
      } catch (e) {
        if (ignore) return;
        const raw = e?.response?.data?.detail;
        setError(
          typeof raw === "string" ? raw : raw?.message || e?.message || "Failed to load node"
        );
      } finally {
        if (!ignore) setLoading(false);
      }
    })();
    return () => { ignore = true; };
  }, [rid, nodeId]);

  const consoleType = String(node?.console_type || "").toLowerCase();
  const host        = node?.console_host || "";
  const port        = node?.console_port || null;

  return (
    <Box sx={pageWrap}>
      {/* Barre de navigation */}
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
        <IconButton onClick={() => navigate(-1)}>
          <ArrowBackIcon />
        </IconButton>
        <Typography variant="h6" fontWeight={900}>
          {t("console_page.title")}
          {node ? ` — ${node.name}` : ""}
        </Typography>
        {rid && (
          <Button variant="outlined" size="small" onClick={() => navigate(`/lab/${rid}`)}>
            {t("console_page.workspace_btn")}
          </Button>
        )}
      </Stack>

      {/* Erreur rid invalide */}
      {!rid && <Alert severity="error">{t("console_page.invalid_id")}</Alert>}

      {/* Chargement */}
      {loading && (
        <Paper elevation={0} sx={{ p: 3, border: "1px solid", borderColor: "divider", borderRadius: 3 }}>
          <Stack direction="row" spacing={2} alignItems="center">
            <CircularProgress size={22} />
            <Typography>{t("console_page.loading")}</Typography>
          </Stack>
        </Paper>
      )}

      {/* Erreur chargement */}
      {!loading && error && <Alert severity="error">{error}</Alert>}

      {/* Console */}
      {!loading && !error && node && (
        <Paper sx={{ p: 2, height: "70vh", display: "flex", flexDirection: "column" }}>
          <Typography fontWeight={900} sx={{ mb: 1 }}>
            {node.name} • {consoleType.toUpperCase()} • :{port}
          </Typography>

          {/* ── Telnet ── */}
          {consoleType === "telnet" && host && port ? (
            <Box sx={{ flex: 1, minHeight: 0 }}>
              <WebTelnetConsole
                host={host}
                port={port}
                reservationId={rid}
                nodeId={nodeId}
              />
            </Box>
          ) : null}

          {/* ── VNC ── */}
          {consoleType === "vnc" && host && port ? (
            <Box sx={{ flex: 1, minHeight: 0 }}>
              <NoVncViewer
                host={host}
                port={port}
                label={`${node.name} VNC`}
              />
            </Box>
          ) : null}

          {/* ── Type non supporté ── */}
          {consoleType !== "telnet" && consoleType !== "vnc" && (
            <Alert severity="warning">
              {t("console_page.unsupported_type", {
                type: consoleType || "unknown",
                link: node.direct_link || node.telnet_url || node.vnc_url || "",
              })}
            </Alert>
          )}
        </Paper>
      )}
    </Box>
  );
}
